import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["state", "rowId", "preventScrollOnKeyDown", "getItem"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback, useRef, useContext, useMemo, useState } from 'react';
import { isTextField, isButton, getScrollingElement } from 'ariakit-utils/dom';
import { isPortalEvent, isSelfTarget } from 'ariakit-utils/events';
import { useId, useEvent, useBooleanEvent, useWrapElement, useForkRef, useSafeLayoutEffect } from 'ariakit-utils/hooks';
import { useStore, createMemoComponent } from 'ariakit-utils/store';
import { createHook, createElement } from 'ariakit-utils/system';
import { useCollectionItem } from '../collection/collection-item.js';
import { useCommand } from '../command/command.js';
import { C as CompositeContext, j as CompositeRowContext, k as getContextId, i as isItem, l as focusSilently, m as CompositeItemContext, f as findEnabledItemById } from '../__utils-7009759c.js';
import { jsx } from 'react/jsx-runtime';

function isEditableElement(element) {
  if (element.isContentEditable) return true;
  if (isTextField(element)) return true;
  return element.tagName === "INPUT" && !isButton(element);
}

function getNextPageOffset(scrollingElement, pageUp) {
  if (pageUp === void 0) {
    pageUp = false;
  }

  var height = scrollingElement.clientHeight;

  var _scrollingElement$get = scrollingElement.getBoundingClientRect(),
      top = _scrollingElement$get.top; // Calculates the size of the page based on the scrolling element's height.
  // This is similar to how browsers calculate the scroll position when pressing
  // spacebar, page up, or page down.


  var pageSize = Math.max(height * 0.875, height - 40) * 1.5;
  var pageOffset = pageUp ? height - pageSize + top : pageSize + top;

  if (scrollingElement.tagName === "HTML") {
    return pageOffset + scrollingElement.scrollTop;
  }

  return pageOffset;
}

function getItemOffset(itemElement, pageUp) {
  if (pageUp === void 0) {
    pageUp = false;
  }

  var _itemElement$getBound = itemElement.getBoundingClientRect(),
      top = _itemElement$getBound.top;

  if (pageUp) {
    // PageUp is always the inverse of PageDown. On PageDown, we consider only
    // the top offset of the element. On PageUp we need to add the height of the
    // element as well so we consider the bottom of it.
    return top + itemElement.clientHeight;
  }

  return top;
}

function findNextPageItemId(element, items, next, pageUp) {
  if (pageUp === void 0) {
    pageUp = false;
  }

  if (!items) return;
  if (!next) return;
  var scrollingElement = getScrollingElement(element);
  if (!scrollingElement) return;
  var nextPageOffset = getNextPageOffset(scrollingElement, pageUp);
  var id;
  var prevDifference; // We need to loop through the next items to find the one that is closest to
  // the next page offset.

  for (var i = 0; i < items.length; i += 1) {
    var previousId = id;
    id = next(i);
    if (!id) break;
    if (id === previousId) continue;
    var item = findEnabledItemById(items, id);
    var itemElement = item == null ? void 0 : item.ref.current;
    if (!itemElement) continue;
    var itemOffset = getItemOffset(itemElement, pageUp);
    var difference = itemOffset - nextPageOffset;
    var absDifference = Math.abs(difference); // On PageUp, the element is at the next page if the difference between its
    // top offset (plus its height) and the next page offset is less than or
    // equal zero. On PageDown, the difference should be greater than or equal
    // zero.

    if (pageUp && difference <= 0 || !pageUp && difference >= 0) {
      // There may be cases when there's a lot of space between the pages, for
      // example, when there's a lot of disabled items. In this case, the first
      // item in the next page might not be the closest one. So we return the
      // previous item id if its difference is less than the current one.
      if (prevDifference !== undefined && prevDifference < absDifference) {
        id = previousId;
      }

      break;
    }

    prevDifference = absDifference;
  }

  return id;
}

function useItem(items, id) {
  return useMemo(function () {
    if (!id) return;
    return items == null ? void 0 : items.find(function (item) {
      return item.id === id;
    });
  }, [items, id]);
}

function targetIsAnotherItem(event, items) {
  if (isSelfTarget(event)) return false;
  var target = event.target;
  return isItem(items, target, event.currentTarget);
}

function useRole(ref, props) {
  var _useState = useState(props.role),
      _useState2 = _slicedToArray(_useState, 2),
      role = _useState2[0],
      setRole = _useState2[1];

  useSafeLayoutEffect(function () {
    var element = ref.current;
    if (!element) return;
    setRole(element.getAttribute("role") || props.role);
  }, [props.role]);
  return role;
}

function requiresAriaSelected(role) {
  return role === "option" || role === "treeitem";
}

function supportsAriaSelected(role) {
  if (role === "option") return true;
  if (role === "tab") return true;
  if (role === "treeitem") return true;
  if (role === "gridcell") return true;
  if (role === "row") return true;
  if (role === "columnheader") return true;
  if (role === "rowheader") return true;
  return false;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a composite item.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useCompositeItem({ state });
 * <Role {...props}>Item 1</Role>
 * ```
 */


var useCompositeItem = createHook(function (_ref) {
  var _state6, _state23, _state24, _state26, _state27;

  var state = _ref.state,
      rowIdProp = _ref.rowId,
      _ref$preventScrollOnK = _ref.preventScrollOnKeyDown,
      preventScrollOnKeyDown = _ref$preventScrollOnK === void 0 ? false : _ref$preventScrollOnK,
      getItemProp = _ref.getItem,
      props = _objectWithoutProperties(_ref, _excluded);

  var id = useId(props.id);
  state = useStore(state || CompositeContext, [useCallback(function (s) {
    return s.activeId === id;
  }, [id]), "baseRef", "items", "virtualFocus", "registerItem", "setActiveId", "orientation", "up", "next", "down", "previous", "first", "last", "move"]);
  var ref = useRef(null);
  var row = useContext(CompositeRowContext);
  var rowId = rowIdProp != null ? rowIdProp : getContextId(state, row);
  var trulyDisabled = props.disabled && !props.accessibleWhenDisabled;
  var getItem = useCallback(function (item) {
    var nextItem = _objectSpread(_objectSpread({}, item), {}, {
      id: id,
      rowId: rowId,
      disabled: !!trulyDisabled
    });

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [id, rowId, trulyDisabled, getItemProp]);
  var onFocusProp = props.onFocus;
  var hasFocusedComposite = useRef(false);
  var onFocus = useEvent(function (event) {
    var _state, _state2, _state4;

    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (isPortalEvent(event)) return;
    if (!id) return; // If the target is another item, this probably means that composite items
    // are nested. This is okay when building, for example, tree or treegrid
    // elements. In this case, we just ignore the focus event on this parent
    // item.

    if ((_state = state) != null && _state.items && targetIsAnotherItem(event, state.items)) return;

    if (((_state2 = state) == null ? void 0 : _state2.activeId) !== id) {
      var _state3;

      (_state3 = state) == null ? void 0 : _state3.setActiveId(id);
    } // When using aria-activedescendant, we want to make sure that the
    // composite container receives focus, not the composite item.


    if (!((_state4 = state) != null && _state4.virtualFocus)) return; // But we'll only do this if the focused element is the composite item
    // itself

    if (!isSelfTarget(event)) return; // and the composite item is not a text field or contenteditable element.

    if (isEditableElement(event.currentTarget)) return;
    var composite = state.baseRef.current;
    if (!composite) return;
    hasFocusedComposite.current = true; // If the previously focused element is a composite or composite item
    // component, we'll transfer focus silently to the composite element.
    // That's because this is just a transition event, the composite element
    // was likely already focused, so we're just immediately returning focus
    // to it when navigating through the items.

    var fromComposite = event.relatedTarget === composite || isItem(state.items, event.relatedTarget);

    if (fromComposite) {
      focusSilently(composite);
    } // Otherwise, the composite element is likely not focused, so we need this
    // focus event to propagate so consumers can use the onFocus prop on
    // <Composite>.
    else {
      composite.focus();
    }
  });
  var onBlurCaptureProp = props.onBlurCapture;
  var onBlurCapture = useEvent(function (event) {
    var _state5;

    onBlurCaptureProp == null ? void 0 : onBlurCaptureProp(event);
    if (event.defaultPrevented) return;

    if ((_state5 = state) != null && _state5.virtualFocus && hasFocusedComposite.current) {
      // When hasFocusedComposite is true, composite has been focused right
      // after focusing on this item. This is an intermediate blur event, so
      // we ignore it.
      hasFocusedComposite.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  });
  var onKeyDownProp = props.onKeyDown;
  var preventScrollOnKeyDownProp = useBooleanEvent(preventScrollOnKeyDown);
  var item = useItem((_state6 = state) == null ? void 0 : _state6.items, id);
  var isGrid = !!(item != null && item.rowId);
  var onKeyDown = useEvent(function (event) {
    var _state7, _state8, _state9, _state10, _state11, _state12;

    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    var isVertical = ((_state7 = state) == null ? void 0 : _state7.orientation) !== "horizontal";
    var isHorizontal = ((_state8 = state) == null ? void 0 : _state8.orientation) !== "vertical";
    var keyMap = {
      ArrowUp: (isGrid || isVertical) && ((_state9 = state) == null ? void 0 : _state9.up),
      ArrowRight: (isGrid || isHorizontal) && ((_state10 = state) == null ? void 0 : _state10.next),
      ArrowDown: (isGrid || isVertical) && ((_state11 = state) == null ? void 0 : _state11.down),
      ArrowLeft: (isGrid || isHorizontal) && ((_state12 = state) == null ? void 0 : _state12.previous),
      Home: function Home() {
        var _state14;

        if (!isGrid || event.ctrlKey) {
          var _state13;

          return (_state13 = state) == null ? void 0 : _state13.first();
        }

        return (_state14 = state) == null ? void 0 : _state14.previous(-1);
      },
      End: function End() {
        var _state16;

        if (!isGrid || event.ctrlKey) {
          var _state15;

          return (_state15 = state) == null ? void 0 : _state15.last();
        }

        return (_state16 = state) == null ? void 0 : _state16.next(-1);
      },
      PageUp: function PageUp() {
        var _state17, _state18;

        return findNextPageItemId(event.currentTarget, (_state17 = state) == null ? void 0 : _state17.items, (_state18 = state) == null ? void 0 : _state18.up, true);
      },
      PageDown: function PageDown() {
        var _state19, _state20;

        return findNextPageItemId(event.currentTarget, (_state19 = state) == null ? void 0 : _state19.items, (_state20 = state) == null ? void 0 : _state20.down);
      }
    };
    var action = keyMap[event.key];

    if (action) {
      var nextId = action();

      if (preventScrollOnKeyDownProp(event) || nextId !== undefined) {
        var _state21;

        event.preventDefault();
        (_state21 = state) == null ? void 0 : _state21.move(nextId);
      }
    }
  });
  var providerValue = useMemo(function () {
    var _state22;

    return {
      id: id,
      baseRef: (_state22 = state) == null ? void 0 : _state22.baseRef
    };
  }, [id, (_state23 = state) == null ? void 0 : _state23.baseRef]);
  props = useWrapElement(props, function (element) {
    return /*#__PURE__*/jsx(CompositeItemContext.Provider, {
      value: providerValue,
      children: element
    });
  }, [providerValue]);
  var isActiveItem = ((_state24 = state) == null ? void 0 : _state24.activeId) === id;
  var role = useRole(ref, props);
  var ariaSelected;

  if (isActiveItem) {
    var _state25;

    if (requiresAriaSelected(role)) {
      // When the active item role _requires_ the aria-selected attribute
      // (e.g., option, treeitem), we always set it to true.
      ariaSelected = true;
    } else if ((_state25 = state) != null && _state25.virtualFocus && supportsAriaSelected(role)) {
      // Otherwise, it will be set to true when virtualFocus is set to true
      // (meaning that the focus will be managed using the
      // aria-activedescendant attribute) and the aria-selected attribute is
      // _supported_ by the active item role.
      ariaSelected = true;
    }
  }

  var shouldTabIndex = !((_state26 = state) != null && _state26.virtualFocus) && isActiveItem || // We don't want to set tabIndex="-1" when using CompositeItem as a
  // standalone component, without state props.
  !((_state27 = state) != null && _state27.items.length);
  props = _objectSpread(_objectSpread({
    id: id,
    "aria-selected": ariaSelected,
    "data-active-item": isActiveItem ? "" : undefined
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    tabIndex: shouldTabIndex ? props.tabIndex : -1,
    onFocus: onFocus,
    onBlurCapture: onBlurCapture,
    onKeyDown: onKeyDown
  });
  props = useCommand(props);
  props = useCollectionItem(_objectSpread(_objectSpread({
    state: state
  }, props), {}, {
    getItem: getItem,
    shouldRegisterItem: !!id ? props.shouldRegisterItem : false
  }));
  return props;
});
/**
 * A component that renders a composite item.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 *   <CompositeItem>Item 3</CompositeItem>
 * </Composite>
 * ```
 */

var CompositeItem = createMemoComponent(function (props) {
  var htmlProps = useCompositeItem(props);
  return createElement("button", htmlProps);
});
export { CompositeItem, useCompositeItem };